import { useEffect, useRef, useState } from "react";
import styles from "./style.module.css";
import ImageLoader from "../ImageLoader";
import { Link } from "react-router-dom";

export default function Modal({ items, breakpoints = { 0: 1, 420: 2, 700: 3, 1000: 4 }, titleLength = 35 }) {
    const containerRef = useRef(null);
    const [visible, setVisible] = useState(4);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [offset, setOffset] = useState(0);
    const draggingPreventClick = useRef(false); // 👈 track if we dragged

    // compute visible items count based on screen width
    const computeVisible = () => {
        const w = window.innerWidth;
        const keys = Object.keys(breakpoints).map(Number).sort((a, b) => a - b);
        let v = breakpoints[keys[0]];
        for (const k of keys) if (w >= k) v = breakpoints[k];
        return v;
    };

    // handle resize
    useEffect(() => {
        const update = () => {
            const v = computeVisible();
            setVisible(v);
            setCurrentPosition((old) => Math.min(old, Math.max(0, Math.ceil(items.length / v) - 1)));
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [items.length, breakpoints]);

    const maxIndex = Math.max(0, Math.ceil(items.length / visible) - 1);

    const goTo = (i) => setCurrentPosition(Math.max(0, Math.min(i, maxIndex)));
    const next = () => goTo(currentPosition + 1);
    const prev = () => goTo(currentPosition - 1);

    // Drag handlers
    const handleStart = (e) => {
        setIsDragging(true);
        setStartX(e.clientX || e.touches?.[0]?.clientX);
        setOffset(0);
        draggingPreventClick.current = false; // reset at start
    };

    const handleMove = (e) => {
        if (!isDragging) return;
        const x = e.clientX || e.touches?.[0]?.clientX;
        const diff = x - startX;
        setOffset(diff);
        if (Math.abs(diff) > 5) {
            draggingPreventClick.current = true; // mark as drag
        }
    };

    const handleEnd = () => {
        if (!isDragging) return;
        const containerWidth = containerRef.current.offsetWidth;
        if (offset > containerWidth / 4) {
            prev();
        } else if (offset < -containerWidth / 4) {
            next();
        }
        setIsDragging(false);
        setOffset(0);
    };

    const handleClickCapture = (e) => {
        if (draggingPreventClick.current) {
            e.preventDefault(); // stop navigation
            e.stopPropagation();
        }
    };

    return (
        <div className={styles.carouselWrapper}>
            <div
                ref={containerRef}
                className={styles.carousel}
                onMouseDown={handleStart}
                onMouseMove={handleMove}
                onMouseUp={handleEnd}
                onMouseLeave={handleEnd}
                onTouchStart={handleStart}
                onTouchMove={handleMove}
                onTouchEnd={handleEnd}
            >
                <div
                    className={styles.parentView}
                    style={{
                        width: `${(items.length / visible) * 100}%`,
                        transform: `translateX(calc(-${(currentPosition * 100) / (items.length / visible)}% + ${offset}px))`,
                        transition: isDragging ? "none" : "transform 0.5s ease",
                        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
                    }}
                >
                    {items.map((data, index) => (
                        <Link
                            key={"v1_" + index}
                            className={styles.item}
                            to={"/p/" + data["item_slug"]}
                            onClickCapture={handleClickCapture} // 👈 prevent click after drag
                        >
                            <ImageLoader
                                className={`${styles.image} lazy-loading`}
                                src={data["item_media"]}
                                alt={data["item_media"]}
                            />

                            <h6 className={styles.pricing}>
                                ₹{data["item_offered_price"]}
                                {data["item_offer"] !== "0" && <s>₹{data["item_price"]}</s>}
                            </h6>

                            <h6 className={styles.title}>{data["item_name"].length > titleLength ? data["item_name"].slice(0, titleLength) + '...' : data["item_name"]}</h6>
                            <div className={styles.tag}>{data["item_tags"]}</div>
                        </Link>
                    ))}
                </div>
            </div>

            <div className={styles.controllerView}>
                <div className={styles.indicator}>
                    {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentPosition(index)}
                            className={`${styles.bullet} ${currentPosition === index ? styles.active : ""}`}
                        />
                    ))}
                </div>

                <div className={styles.buttons}>
                    <button className={`${styles.button} ${styles.prev}`} onClick={prev} disabled={currentPosition === 0}>
                        <span className="material-symbols-outlined">arrow_back_ios_new</span>
                    </button>
                    <button className={`${styles.button} ${styles.next}`} onClick={next} disabled={currentPosition === maxIndex}>
                        <span className="material-symbols-outlined">arrow_forward_ios</span>
                    </button>
                </div>
            </div>
        </div>
    );
}